<template>
  <Teleport to="body">
    <div v-if="modelValue" class="cms-media-picker-overlay" @click="close">
      <div class="cms-media-picker-modal" @click.stop>
      <div class="cms-media-picker-header">
        <h3>{{ $t('cms.media.selectFromLibrary') }}</h3>
        <button class="cms-btn-icon" @click="close">
          <v-icon icon="x" size="sm" />
        </button>
      </div>

      <div class="cms-media-picker-filters">
        <div class="cms-filter-group">
          <label>{{ $t('cms.media.filterByType') }}</label>
          <select v-model="selectedType" class="cms-select" @change="loadMedia">
            <option value="">{{ $t('general.all') }}</option>
            <option value="image">{{ $t('cms.media.types.image') }}</option>
            <option value="video">{{ $t('cms.media.types.video') }}</option>
            <option value="document">{{ $t('cms.media.types.document') }}</option>
          </select>
        </div>
        
        <div class="cms-filter-group">
          <label>{{ $t('cms.media.search') }}</label>
          <input 
            v-model="searchQuery" 
            type="text" 
            class="cms-input"
            :placeholder="$t('cms.media.searchPlaceholder')"
            @input="debouncedSearch"
          />
        </div>
      </div>

      <div v-if="isLoading" class="cms-media-picker-loading">
        <div class="cms-loading-spinner" />
        <p>{{ $t('general.loading') }}</p>
      </div>

      <div v-else-if="errorMessage" class="cms-media-picker-error">
        <p>{{ errorMessage }}</p>
        <button class="cms-btn-link" type="button" @click="loadMedia">
          {{ $t('general.retry') }}
        </button>
      </div>

      <div v-else-if="mediaList.length === 0" class="cms-media-picker-empty">
        <p>{{ $t('cms.media.emptyState') }}</p>
      </div>

      <div v-else class="cms-media-picker-grid">
        <div 
          v-for="media in mediaList" 
          :key="media.id"
          class="cms-media-picker-item"
          :class="{ 
            'selected': props.multiple ? selectedMediaIds.includes(media.id) : selectedMediaId === media.id 
          }"
          @click="selectMedia(media)"
        >
          <div class="cms-media-picker-preview">
            <img 
              v-if="getMediaType(media) === 'image'" 
              :src="getMediaUrl(media)" 
              :alt="media.name"
              class="cms-media-picker-image"
            />
            <video 
              v-else-if="getMediaType(media) === 'video'" 
              :src="getMediaUrl(media)" 
              class="cms-media-picker-video"
            />
            <div v-else class="cms-media-picker-document">
              <v-icon icon="file" size="xl" />
              <span>{{ getFileExtension(media.fileName || media.file_name || media.name || '') }}</span>
            </div>
            <div 
              v-if="props.multiple ? selectedMediaIds.includes(media.id) : selectedMediaId === media.id" 
              class="cms-media-picker-selected-badge"
            >
              <v-icon icon="check" size="sm" />
            </div>
            <div 
              v-if="props.multiple && selectedMediaIds.includes(media.id)" 
              class="cms-media-picker-selection-number"
            >
              {{ selectedMediaIds.indexOf(media.id) + 1 }}
            </div>
          </div>
          <div class="cms-media-picker-name">{{ media.name || media.fileName || media.file_name || 'Untitled' }}</div>
        </div>
      </div>

      <cms-pagination
        v-if="paginationMeta"
        id="cms-media-picker-pagination"
        class="cms-media-picker-pagination"
        :meta="paginationMeta"
        :per-page-options="[9, 18, 27, 36]"
        @page-changed="goToPage"
        @per-page-changed="handlePerPageChange"
      />

      <div class="cms-media-picker-actions">
        <div v-if="multiple && selectedMediaList.length > 0" class="cms-media-picker-selection-count">
          {{ selectedMediaList.length }} {{ selectedMediaList.length === 1 ? 'item' : 'items' }} selected
        </div>
        <button class="cms-btn cms-btn-secondary" @click="close">
          {{ $t('general.button.cancel') }}
        </button>
        <button 
          class="cms-btn cms-btn-primary" 
          :disabled="multiple ? selectedMediaList.length === 0 : !selectedMedia"
          @click="confirmSelection"
        >
          {{ multiple ? (selectedMediaList.length > 0 ? `Select (${selectedMediaList.length})` : $t('general.button.select')) : $t('general.button.select') }}
        </button>
      </div>
    </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import api from '@/api';
import { normalizeResource } from '@/composables/useCmsActivityData';
import { extractCmsListData, extractCmsPaginationMeta } from '@/helpers/cmsPagination';
import { dedupeMediaList } from '@/helpers/cmsMediaUpload';
import type { CmsPaginationMeta } from '@/helpers/cmsPagination';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import CmsPagination from '@/components/cms/CmsPagination.vue';

interface Media {
  id: number;
  name: string;
  fileName?: string;
  file_name?: string;
  mimeType?: string;
  mime_type?: string;
  size: number;
  createdAt?: string;
  created_at?: string;
  originalUrl?: string;
  original_url?: string;
  storageUrl?: string;
  storage_url?: string;
  url?: string;
  previewUrl?: string;
  preview_url?: string;
}

interface Props {
  modelValue: boolean;
  filterType?: 'image' | 'video' | 'document' | '';
  multiple?: boolean;
  maxSelection?: number;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  filterType: '',
  multiple: false,
  maxSelection: undefined,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'selected', media: Media | Media[]): void;
}>();

const authStore = useAuthStore();
const agencySlug = computed(() => authStore.agency?.slug ?? null);

const mediaList = ref<Media[]>([]);
const isLoading = ref(false);
const errorMessage = ref('');
const selectedType = ref(props.filterType);
const searchQuery = ref('');
const currentPage = ref(1);
const perPage = ref(24);
const paginationMeta = ref<CmsPaginationMeta | null>(null);
const selectedMediaId = ref<number | null>(null);
const selectedMedia = ref<Media | null>(null);
const selectedMediaIds = ref<number[]>([]);
const selectedMediaList = ref<Media[]>([]);

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const getMediaUrl = (media: Media): string => {
  return media.originalUrl || media.original_url ||
    media.storageUrl || media.storage_url ||
    media.url ||
    media.previewUrl || media.preview_url || '';
};

const getMediaType = (media: Media): 'image' | 'video' | 'document' => {
  const mimeType = media.mimeType || (media as any).mime_type;
  if (!mimeType) return 'document';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  return 'document';
};

const getFileExtension = (fileName: string): string => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
};

const loadMedia = async () => {
  if (!agencySlug.value) {
    errorMessage.value = 'Agency not found';
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const filters: Record<string, any> = {
      perPage: perPage.value,
      page: currentPage.value,
    };

    const typeToUse = selectedType.value || props.filterType;
    if (typeToUse) {
      filters.filters = { type: typeToUse };
    }

    if (searchQuery.value) {
      if (!filters.filters) filters.filters = {};
      filters.filters.search = searchQuery.value;
    }

    const response = await api.media.list(agencySlug.value, filters);

    const list = extractCmsListData(response);
    mediaList.value = dedupeMediaList(list
      .map((item) => {
        const normalized = normalizeResource(item) || item;
        return {
          id: normalized.id,
          name: normalized.name,
          fileName: normalized.fileName ?? normalized.file_name,
          file_name: normalized.file_name ?? normalized.fileName,
          mimeType: normalized.mimeType ?? normalized.mime_type,
          mime_type: normalized.mime_type ?? normalized.mimeType,
          size: normalized.size ?? 0,
          createdAt: normalized.createdAt ?? normalized.created_at,
          created_at: normalized.created_at ?? normalized.createdAt,
          originalUrl: normalized.originalUrl ?? normalized.original_url,
          original_url: normalized.original_url ?? normalized.originalUrl,
          storageUrl: normalized.storageUrl ?? normalized.storage_url,
          storage_url: normalized.storage_url ?? normalized.storageUrl,
          url: normalized.url,
          previewUrl: normalized.previewUrl ?? normalized.preview_url,
          preview_url: normalized.preview_url ?? normalized.previewUrl,
        } as Media;
      })
      .filter((item) => item.id != null));

    const meta = extractCmsPaginationMeta(response, {
      currentPage: currentPage.value,
      perPage: perPage.value,
    });

    if (meta) {
      paginationMeta.value = meta;
      currentPage.value = meta.currentPage;
      perPage.value = meta.perPage;
    } else {
      paginationMeta.value = null;
    }
  } catch (error: any) {
    console.error('Error loading media:', error);
    errorMessage.value = error?.message || 'Failed to load media';
    mediaList.value = [];
  } finally {
    isLoading.value = false;
  }
};

const debouncedSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  searchTimeout = setTimeout(() => {
    currentPage.value = 1;
    loadMedia();
  }, 500);
};

const goToPage = (page: number) => {
  currentPage.value = page;
  loadMedia();
};

const handlePerPageChange = (newPerPage: number) => {
  perPage.value = newPerPage;
  currentPage.value = 1;
  loadMedia();
};

const selectMedia = (media: Media) => {
  if (props.multiple) {
    const index = selectedMediaIds.value.indexOf(media.id);
    if (index > -1) {
      // Deselect
      selectedMediaIds.value.splice(index, 1);
      selectedMediaList.value.splice(index, 1);
    } else {
      // Select (check max selection limit)
      if (props.maxSelection && selectedMediaIds.value.length >= props.maxSelection) {
        return; // Don't allow selection if max is reached
      }
      selectedMediaIds.value.push(media.id);
      selectedMediaList.value.push(media);
    }
  } else {
    selectedMediaId.value = media.id;
    selectedMedia.value = media;
  }
};

const confirmSelection = () => {
  if (props.multiple) {
    if (selectedMediaList.value.length > 0) {
      emit('selected', selectedMediaList.value);
      close();
    }
  } else {
    if (selectedMedia.value) {
      emit('selected', selectedMedia.value);
      close();
    }
  }
};

const close = () => {
  emit('update:modelValue', false);
  selectedMediaId.value = null;
  selectedMedia.value = null;
  selectedMediaIds.value = [];
  selectedMediaList.value = [];
};

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    currentPage.value = 1;
    selectedMediaId.value = null;
    selectedMedia.value = null;
    selectedMediaIds.value = [];
    selectedMediaList.value = [];
    loadMedia();
  }
});

watch(() => props.filterType, (newValue) => {
  if (newValue && !selectedType.value) {
    selectedType.value = newValue;
  }
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/tokens.scss';

.cms-media-picker-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.cms-media-picker-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  color: #333;
}

.cms-media-picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  
  h3 {
    margin: 0;
    font-size: 1.3rem;
  }
}

.cms-media-picker-filters {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;
  flex-wrap: wrap;
}

.cms-filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
  
  label {
    font-size: 0.9rem;
    font-weight: 500;
    color: #333;
  }
}

.cms-media-picker-loading,
.cms-media-picker-error,
.cms-media-picker-empty {
  text-align: center;
  padding: 3rem;
  color: #666;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.cms-loading-spinner {
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid $cms-primary-color;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.cms-media-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.cms-media-picker-item {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: $cms-primary-color;
    transform: translateY(-2px);
    box-shadow: $cms-shadow-sm;
  }
  
  &.selected {
    border-color: $cms-primary-color;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
}

.cms-media-picker-preview {
  position: relative;
  height: 120px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .cms-media-picker-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .cms-media-picker-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .cms-media-picker-document {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: #666;
    font-size: 0.8rem;
  }
}

.cms-media-picker-selected-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: $cms-primary-color;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: $cms-shadow-sm;
}

.cms-media-picker-selection-number {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.cms-media-picker-name {
  padding: 0.5rem;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #f9f9f9;
}

.cms-media-picker-pagination {
  padding: 0 $cms-spacing-md;
  border-top: 1px solid #eee;
  margin-top: 0;
}

.cms-media-picker-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #eee;
}

.cms-media-picker-selection-count {
  color: $cms-primary-color;
  font-weight: 500;
  font-size: 0.9rem;
}

.cms-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &-primary {
    background: $cms-primary-color;
    color: white;

    i, svg, .icon {
      color: white;
    }
    
    &:hover:not(:disabled) {
      background: $cms-primary-dark;
      transform: translateY(-2px);
      box-shadow: $cms-shadow-md;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  &-secondary {
    background: #f5f5f5;
    color: #333;
    
    &:hover:not(:disabled) {
      background: #e0e0e0;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  &-icon {
    background: none;
    border: none;
    color: #333;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 6px;
    transition: all 0.3s ease;
    
    &:hover {
      background: #f5f5f5;
    }
  }

  &-link {
    background: none;
    border: none;
    color: $cms-primary-color;
    cursor: pointer;
    text-decoration: underline;
    
    &:hover {
      color: $cms-primary-dark;
    }
  }
}

.cms-input,
.cms-select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: $cms-primary-color;
  }
}
</style>

